import { CommissionStatuses } from 'constants/commissionStatuses';
import { Resources } from 'constants/resources';

import { useState } from 'react';

import { usePermissions } from 'hooks/usePermissions';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useGetList, useRecordContext } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { Tab } from 'shared/mui/Tab/Tab';
import { TabPanel } from 'shared/mui/TabPanel/TabPanel';
import { Tabs } from 'shared/mui/Tabs/Tabs';
import { useGetFiles } from 'shared/widgets/TableFileViewer/hooks/useGetFiles';
import { Commission } from 'types/embedded/commission';
import { CommissionRecalculationTask } from 'types/embedded/commissionRecalculationTask';

import { CommissionsShowActiveSteps } from './CommissionsTabsActiveSteps/CommissionsTabsActiveSteps';
import { CommissionsTabsComments } from './CommissionsTabsComments';
import { CommissionsTabsConditions } from './CommissionsTabsConditions';
import { CommissionsTabsFiles } from './CommissionsTabsFiles';
import { CommissionsTabsInfo } from './CommissionsTabsInfo';
import { CommissionsTabsLadder } from './CommissionsTabsLadder/CommissionsTabsLadder';
import { CommissionsTabsTask } from './CommissionsTabsTask/CommissionsTabsTask';

type Props = {
  omit?: string;
  relatedField: React.ReactNode;
  initialTab?: number;
  handleTabChange?: (newTab: number) => void;
};

export const CommissionsTabs: React.FC<Props> = ({
  omit,
  relatedField,
  initialTab = 0,
  handleTabChange,
}) => {
  const translate = useTypedTranslate();
  const { hasResourceAccess } = usePermissions();

  const record = useRecordContext<Commission>();

  const isImplementedLadderCommission =
    record?.ladder && record?.status?.value === CommissionStatuses.Implemented;
  const isTaskTabVisible =
    !!record?.parent && hasResourceAccess(Resources.CommissionRecalculationTasks);

  const { data: files } = useGetFiles(Resources.Commissions, record?.originId);
  const { data: tasks } = useGetList<CommissionRecalculationTask>(
    Resources.CommissionRecalculationTasks,
    {
      filter: { commission: record?.id },
    },
    { enabled: isTaskTabVisible },
  );

  const [tab, setTab] = useState(initialTab === 4 && !isTaskTabVisible ? 0 : initialTab);

  const handleChange = (_e: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
    handleTabChange?.(newValue);
  };

  return (
    <>
      <Box sx={{ width: '100%', marginBottom: '24px' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs onChange={handleChange} value={tab}>
            <Tab label={translate('app.common.info')} value={0} />
            <Tab label={translate('app.commissions.conditions')} value={1} />
            {isImplementedLadderCommission && <Tab label="Активные ступени по методам" value={5} />}
            <Tab label={`${translate('app.common.files')} (${files?.length || 0})`} value={2} />
            <Tab
              label={`${translate('app.common.comments')} (${record?.commentCount || 0})`}
              value={3}
            />
            {isTaskTabVisible && (
              <Tab
                label={`${translate('app.commissions.recalculationTasks')} (${tasks?.length || 0})`}
                value={4}
              />
            )}
          </Tabs>
        </Box>
      </Box>
      <TabPanel index={0} value={tab}>
        <CommissionsTabsInfo omit={omit} relatedField={relatedField} />
      </TabPanel>
      <TabPanel index={1} value={tab}>
        {record?.ladder ? <CommissionsTabsLadder /> : <CommissionsTabsConditions />}
      </TabPanel>
      {isImplementedLadderCommission && (
        <TabPanel index={5} value={tab}>
          <CommissionsShowActiveSteps />
        </TabPanel>
      )}
      <TabPanel index={2} value={tab}>
        <CommissionsTabsFiles />
      </TabPanel>
      <TabPanel index={3} value={tab}>
        <CommissionsTabsComments />
      </TabPanel>
      {isTaskTabVisible && (
        <TabPanel index={4} value={tab}>
          <CommissionsTabsTask />
        </TabPanel>
      )}
    </>
  );
};
